import {
  FamiliesApi,
  FamilyResponse,
  CreateFamilyRequest,
  UpdateFamilyRequest,
  FamilyListResponse,
  FamilyMembersResponse,
  FamilyRelationshipsResponse,
  FamilyVersionResponse,
  FamilyUsersResponse,
  FamilyUserResponse,
  DisplayNameResponse,
  UpdateDisplayNameRequest,
  UpdateFamilyUserRoleRequest
} from 'we-tree-api-client/v2';

import { getApiConfiguration } from '@/services/SessionService';
import { ApiResult, withApiSetup } from './apiWrapper';

export type {
  DisplayNameResponse,
  UpdateDisplayNameRequest,
  FamilyUserResponse,
  FamilyUsersResponse,
  UpdateFamilyUserRoleRequest
};

/**
 * Wrapped families API with standardized error handling
 */
class WrappedFamiliesApi {
  /**
   * Create a new family
   */
  async createFamily(familyData: CreateFamilyRequest): Promise<ApiResult<FamilyResponse>> {
    return withApiSetup(
      (config) => new FamiliesApi(config),
      getApiConfiguration,
      (api) => api.createFamily(familyData)
    );
  }

  /**
   * Get a family by ID
   */
  async getFamily(familyId: string): Promise<ApiResult<FamilyResponse>> {
    return withApiSetup(
      (config) => new FamiliesApi(config),
      getApiConfiguration,
      (api) => api.getFamily(familyId)
    );
  }

  /**
   * Search families
   */
  async searchFamilies(): Promise<ApiResult<FamilyListResponse>> {
    return withApiSetup(
      (config) => new FamiliesApi(config),
      getApiConfiguration,
      (api) => api.searchFamilies()
    );
  }

  /**
   * Update a family
   */
  async updateFamily(familyId: string, familyData: UpdateFamilyRequest): Promise<ApiResult<FamilyResponse>> {
    return withApiSetup(
      (config) => new FamiliesApi(config),
      getApiConfiguration,
      (api) => api.updateFamily(familyId, familyData)
    );
  }

  /**
   * Delete a family
   * Note: This method throws on error to maintain compatibility with existing usage
   */
  async deleteFamily(familyId: string): Promise<void> {
    const result = await withApiSetup(
      (config) => new FamiliesApi(config),
      getApiConfiguration,
      (api) => api.deleteFamily(familyId)
    );

    if (!result.success) {
      throw result.error;
    }
  }

  /**
   * Get families for the current user
   */
  async getUserFamilies(): Promise<ApiResult<FamilyResponse[]>> {
    return withApiSetup(
      (config) => new FamiliesApi(config),
      getApiConfiguration,
      (api) => api.getUserFamilies()
    );
  }

  /**
   * Get family members
   */
  async getFamilyMembers(familyId: string): Promise<ApiResult<FamilyMembersResponse>> {
    return withApiSetup(
      (config) => new FamiliesApi(config),
      getApiConfiguration,
      (api) => api.getFamilyMembers(familyId)
    );
  }

  /**
   * Get family relationships
   */
  async getFamilyRelationships(familyId: string): Promise<ApiResult<FamilyRelationshipsResponse>> {
    return withApiSetup(
      (config) => new FamiliesApi(config),
      getApiConfiguration,
      (api) => api.getFamilyRelationships(familyId)
    );
  }

  /**
   * Get the effective display name for a family (for the current user)
   */
  async getDisplayName(familyId: string): Promise<ApiResult<DisplayNameResponse>> {
    return withApiSetup(
      (config) => new FamiliesApi(config),
      getApiConfiguration,
      (api) => api.getDisplayName(familyId)
    );
  }

  /**
   * Set the user's alternative display name for a family
   */
  async setDisplayName(familyId: string, request: UpdateDisplayNameRequest): Promise<ApiResult<DisplayNameResponse>> {
    return withApiSetup(
      (config) => new FamiliesApi(config),
      getApiConfiguration,
      (api) => api.setDisplayName(familyId, request)
    );
  }

  /**
   * Clear the user's alternative display name for a family
   */
  async deleteDisplayName(familyId: string): Promise<ApiResult<DisplayNameResponse>> {
    return withApiSetup(
      (config) => new FamiliesApi(config),
      getApiConfiguration,
      (api) => api.deleteDisplayName(familyId)
    );
  }

  /**
   * Get family version (latest updatedAt timestamp)
   * Used to check if family data has changed since last load
   */
  async getFamilyVersion(familyId: string): Promise<ApiResult<FamilyVersionResponse>> {
    return withApiSetup(
      (config) => new FamiliesApi(config),
      getApiConfiguration,
      (api) => api.getFamilyVersion(familyId)
    );
  }

  /**
   * Get family users with their roles for the overview.
   * Email is included only for owner/admin roles when the caller is owner or admin.
   * All viewers receive nickname and linked memberName when available.
   */
  async getFamilyUsers(familyId: string): Promise<ApiResult<FamilyUsersResponse>> {
    return withApiSetup(
      (config) => new FamiliesApi(config),
      getApiConfiguration,
      (api) => api.getFamilyUsers(familyId)
    );
  }

  /**
   * Update a collaborator's family role (equal-or-lesser; tier-gated on API).
   */
  async updateFamilyUserRole(
    familyId: string,
    userId: string,
    body: UpdateFamilyUserRoleRequest
  ): Promise<ApiResult<FamilyUserResponse>> {
    return withApiSetup(
      (config) => new FamiliesApi(config),
      getApiConfiguration,
      (api) => api.updateFamilyUserRole(familyId, userId, body)
    );
  }
}

/**
 * Wrapped families API instance with standardized error handling
 */
export const familiesApi = new WrappedFamiliesApi();

export default familiesApi;
